import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Space, Table, Tooltip } from "antd";
import AddMilitaryModal from "features/military/addMilitaryModal";
import {
  useDeleteDebtorMilitaryMutation,
  useFetchAllDebtorMilitaryQuery,
} from "features/military/militaryAPI";

import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useState } from "react";
import styled from "styled-components";
import { camelToWords, formatDate, snakeToCamelCase } from "common/utils";

const StyledButton = styled(Button)`
  margin-bottom: 12px;
`;

const StyledTable = styled(Table)`
  & .ant-table-cell:not(th) {
    padding: 0px 8px !important;
  }
`;

function MilitaryTable({ debtorId }) {
  const [isMilitaryModalVisible, setMilitaryModalVisible] = useState(false);
  const [deleteMilitary] = useDeleteDebtorMilitaryMutation();
  const [activeRecord, setActiveRecord] = useState(null);
  const { data: militaryRecords, isLoading } = useFetchAllDebtorMilitaryQuery({ debtorId });

  const {
    data: {
      [PERMISSIONS.DEBTOR_MILITARY__GET]: isMilitaryGetAuthorized,
      [PERMISSIONS.DEBTOR_MILITARY__CREATE]: isMilitaryCreateAuthorized,
      [PERMISSIONS.DEBTOR_MILITARY__UPDATE]: isMilitaryUpdateAuthorized,
      [PERMISSIONS.DEBTOR_MILITARY__DELETE]: isMilitaryDeleteAuthorized,
    },
  } = useAuthorizations();

  const onUpdateMilitary = (record) => {
    setActiveRecord(record);
    setMilitaryModalVisible(true);
  };

  const handleDelete = async ({ debtorId: debtorIdParam, militaryId }) => {
    try {
      await deleteMilitary({ debtorId: debtorIdParam, militaryId }).unwrap();
    } catch (error) {
      console.error("Failed to delete military record:", error);
    }
  };

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => camelToWords(snakeToCamelCase(record?.status)) ?? "-",
    },
    {
      title: "Service Branch",
      dataIndex: "serviceBranchDescription",
      key: "serviceBranchDescription",
      render: (text, record) => record?.serviceBranchDescription || "-",
    },
    {
      title: "Active Duty Start Date",
      dataIndex: "activeDutyStartDate",
      key: "activeDutyStartDate",
      render: (text, record) =>
        record?.activeDutyStartDate ? formatDate(record.activeDutyStartDate) : "-",
    },
    {
      title: "Active Duty End Date",
      dataIndex: "activeDutyEndDate",
      key: "activeDutyEndDate",
      render: (text, record) =>
        record?.activeDutyEndDate ? formatDate(record.activeDutyEndDate) : "-",
    },
    {
      title: "Scrub Date",
      dataIndex: "scrubDate",
      key: "scrubDate",
      render: (text, record) => (record?.scrubDate ? formatDate(record.scrubDate) : "-"),
    },
    {
      title: "Certificate ID",
      dataIndex: "certificateId",
      key: "certificateId",
      render: (text, record) => record?.certificateId || "-",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      hidden: !isMilitaryDeleteAuthorized && !isMilitaryUpdateAuthorized,
      render: (text, record) => {
        return (
          <Space size="middle">
            {isMilitaryUpdateAuthorized && (
              <Tooltip placement="bottom" title="Edit" key="edit">
                <EditOutlined key="edit" onClick={() => onUpdateMilitary(record)} />
              </Tooltip>
            )}
            {isMilitaryDeleteAuthorized && (
              <Popconfirm
                placement="topLeft"
                okText="Yes"
                title="Are you sure you want to delete this record?"
                onConfirm={() => handleDelete({ debtorId, militaryId: record.id })}
              >
                <DeleteOutlined key="delete" />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ].filter((column) => !column.hidden);

  const onMilitaryOk = () => {
    setMilitaryModalVisible(false);
    setActiveRecord(null);
  };

  const onMilitaryCancel = () => {
    setMilitaryModalVisible(false);
    setActiveRecord(null);
  };

  return isMilitaryGetAuthorized ? (
    <>
      {isMilitaryCreateAuthorized && (
        <StyledButton
          type="link"
          onClick={() => setMilitaryModalVisible(true)}
          icon={<PlusOutlined />}
        >
          Add Military
        </StyledButton>
      )}
      <StyledTable
        bordered
        loading={isLoading}
        style={{ cursor: "pointer" }}
        // @ts-ignore
        columns={columns}
        dataSource={militaryRecords}
        scroll={{ x: "max-content" }}
      />
      {isMilitaryModalVisible && (
        <AddMilitaryModal
          militaryRecord={activeRecord}
          debtorId={debtorId}
          title={activeRecord ? "Edit Military" : "Add Military"}
          open={isMilitaryModalVisible}
          onOk={onMilitaryOk}
          onCancel={onMilitaryCancel}
        />
      )}
    </>
  ) : null;
}

export default MilitaryTable;
